import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { FileText, Plus, Download, Upload, Trash2, Pencil, X, TrendingUp, FileCheck2, Receipt } from 'lucide-react';

const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toFixed(0)}`;
};

export default function B2BRevenuePanel({ outletName, b2b, onChange }) {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ deal_id: '', deal_name: '', month: '', amount: '', invoice_number: '', po_number: '', recognised_date: '', notes: '' });
  const [generatingId, setGeneratingId] = useState(null);

  const refresh = async () => {
    try {
      const res = await api.get(`/b2b-revenue?outlet=${encodeURIComponent(outletName)}`);
      setEntries(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { refresh(); }, [outletName]);

  const resetForm = () => {
    setForm({ deal_id: '', deal_name: '', month: '', amount: '', invoice_number: '', po_number: '', recognised_date: '', notes: '' });
    setEditId(null);
    setShowForm(false);
  };

  const generateSchedule = async (contract) => {
    if (!contract.contract_start_date || !contract.contract_end_date) {
      return alert(`Contract "${contract.deal_name}" needs contract_start_date and contract_end_date.\n\nEdit the deal in the Pipeline tab first.`);
    }
    if (!window.confirm(`Auto-project monthly revenue for "${contract.deal_name}"?\n\n${fC(contract.deal_value)} ÷ months in ${contract.contract_start_date} → ${contract.contract_end_date} will be scheduled as "projected" entries. Any existing realised entries stay untouched.`)) return;
    setGeneratingId(contract.id);
    try {
      const res = await api.post('/b2b-revenue/generate-schedule', { deal_id: contract.id });
      alert(`✓ Scheduled ${res.data.projected_inserted} projected entries (${fC(res.data.monthly_amount)}/month over ${res.data.months_covered} months). ${res.data.realised_preserved} realised entries preserved.`);
      refresh();
      if (onChange) onChange();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
    setGeneratingId(null);
  };

  const submit = async () => {
    if (!form.month || !form.amount) return alert('Month and amount are required');
    try {
      const payload = { ...form, amount: parseFloat(form.amount), entry_type: 'realised' };
      if (editId) {
        await api.put(`/b2b-revenue/${editId}`, payload);
      } else {
        await api.post('/b2b-revenue', { outlet: outletName, ...payload });
      }
      resetForm();
      refresh();
      if (onChange) onChange();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  const startEdit = (e) => {
    setForm({ deal_id: e.deal_id || '', deal_name: e.deal_name || '', month: e.month || '', amount: String(e.amount || ''), invoice_number: e.invoice_number || '', po_number: e.po_number || '', recognised_date: e.recognised_date || '', notes: e.notes || '' });
    setEditId(e.id);
    setShowForm(true);
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete ₹${(e.amount / 100000).toFixed(1)}L realised entry for ${e.deal_name || e.month}?`)) return;
    await api.delete(`/b2b-revenue/${e.id}`);
    refresh();
    if (onChange) onChange();
  };

  const exportCSV = async () => {
    const res = await api.get(`/b2b-revenue/export-csv?outlet=${encodeURIComponent(outletName)}`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `b2b_revenue_${outletName}.csv`; a.click();
  };

  const downloadTemplate = async () => {
    const res = await api.get('/b2b-revenue/csv-template', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'b2b_revenue_template.csv'; a.click();
  };

  const importCSV = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/b2b-revenue/import-csv', fd);
    alert(`Imported ${res.data.created} entries${res.data.errors.length ? ` (${res.data.errors.length} errors)` : ''}`);
    refresh();
    if (onChange) onChange();
  };

  const contracted = b2b?.contracted || 0;
  const realised = b2b?.realised_ytd || 0;
  const pct = contracted > 0 ? Math.min(100, (realised / contracted) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="b2b-revenue-panel">
      {/* Top summary: Contracted vs Realised */}
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#1B2A4A' }}>B2B Revenue</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>Contracted (signed) vs Realised (invoiced & recognised)</p>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="b2b-add-btn">
              <Plus size={12} /> Record Revenue
            </button>
            <button onClick={downloadTemplate} className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="b2b-template-btn">
              <Download size={11} /> Template
            </button>
            <button onClick={exportCSV} className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="b2b-export-btn">
              <Download size={11} /> Export
            </button>
            <label className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] border hover:bg-gray-50 cursor-pointer" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
              <Upload size={11} /> Import
              <input type="file" accept=".csv" onChange={e => importCSV(e.target.files?.[0])} className="hidden" data-testid="b2b-import-input" />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#FFFBEB' }} data-testid="b2b-contracted-kpi">
            <div className="flex items-center gap-1.5 mb-1">
              <FileCheck2 size={11} style={{ color: '#BA7517' }} />
              <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#BA7517' }}>Contracted</p>
            </div>
            <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{fC(contracted)}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{b2b?.contracts?.length || 0} signed deal{b2b?.contracts?.length === 1 ? '' : 's'}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#ECFDF5' }} data-testid="b2b-realised-kpi">
            <div className="flex items-center gap-1.5 mb-1">
              <Receipt size={11} style={{ color: '#2D8B5E' }} />
              <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#2D8B5E' }}>Realised YTD</p>
            </div>
            <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{fC(realised)}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{pct.toFixed(0)}% of contracted</p>
            {(b2b?.projected_ytd > 0) && <p className="text-[10px] mt-0.5 italic" style={{ color: '#BA7517' }}>+ {fC(b2b.projected_ytd)} projected</p>}
          </div>
          <div className="p-3 rounded-lg sm:col-span-1 col-span-2" style={{ backgroundColor: '#F3F4F6' }} data-testid="b2b-progress-kpi">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={11} style={{ color: '#378ADD' }} />
              <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#378ADD' }}>This Month</p>
            </div>
            <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{fC(b2b?.realised_mtd || 0)}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>Quarter: {fC(b2b?.realised_qtd || 0)}</p>
          </div>
        </div>

        {/* Contracted progress bar */}
        {contracted > 0 && (
          <div className="mt-3">
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
              <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#2D8B5E' }} />
            </div>
            <div className="flex justify-between text-[10px] mt-1" style={{ color: '#6B7280' }}>
              <span>Realised: {fC(realised)}</span>
              <span>Unrealised: {fC(contracted - realised)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Contracts list */}
      {b2b?.contracts?.length > 0 && (
        <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-contracts-list">
          <div className="px-4 py-2.5 border-b" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
              <FileCheck2 size={12} /> Active Contracts ({b2b.contracts.length})
            </p>
          </div>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {b2b.contracts.map(c => {
              const realised_for = entries.filter(e => e.deal_name === c.deal_name).reduce((s, e) => s + (e.amount || 0), 0);
              const cpct = c.deal_value > 0 ? Math.min(100, (realised_for / c.deal_value) * 100) : 0;
              return (
                <div key={c.id} className="px-4 py-2.5" data-testid={`b2b-contract-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate" style={{ color: '#1B2A4A' }}>{c.deal_name || c.client_company}</p>
                        {c.stage === 'Pending Renewal' && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ backgroundColor: '#FEF3C7', color: '#BA7517' }}>PENDING RENEWAL</span>
                        )}
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                        {c.entity && `${c.entity} · `}
                        {c.client_company && c.deal_name && c.client_company !== c.deal_name && `${c.client_company} · `}
                        {c.owner && `Owned by ${c.owner}`}
                        {c.contract_start_date && ` · ${c.contract_start_date} → ${c.contract_end_date || '—'}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(c.deal_value)}</p>
                      <p className="text-[10px]" style={{ color: realised_for >= c.deal_value ? '#2D8B5E' : '#BA7517' }}>
                        {fC(realised_for)} realised ({cpct.toFixed(0)}%)
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="h-full" style={{ width: `${cpct}%`, backgroundColor: cpct >= 100 ? '#2D8B5E' : '#378ADD' }} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3">
                    <button onClick={() => { resetForm(); setForm(f => ({ ...f, deal_id: c.id, deal_name: c.deal_name })); setShowForm(true); }} className="text-[10px] font-medium" style={{ color: '#2A7B88' }} data-testid={`b2b-record-${c.id}`}>
                      + Record revenue for this contract
                    </button>
                    <button onClick={() => generateSchedule(c)} disabled={generatingId === c.id} className="text-[10px] font-medium disabled:opacity-50" style={{ color: '#BA7517' }} data-testid={`b2b-schedule-${c.id}`}>
                      {generatingId === c.id ? '⏳ Projecting...' : '📅 Auto-project monthly'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border-2 p-4 space-y-3" style={{ borderColor: '#2A7B88' }} data-testid="b2b-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{editId ? 'Edit Realised Revenue' : 'Record Realised Revenue'}</p>
            <button onClick={resetForm}><X size={16} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Contract / Deal</label>
              <select value={form.deal_name} onChange={e => setForm(f => ({ ...f, deal_name: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-deal">
                <option value="">— Select contract —</option>
                {b2b?.contracts?.map(c => <option key={c.id} value={c.deal_name}>{`${c.deal_name} (${fC(c.deal_value)})`}</option>)}
                <option value="_other">Other / unlinked</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Month *</label>
              <input type="month" value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-month" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Amount (₹) *</label>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 11200000 for ₹1.12Cr" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-amount" />
              {form.amount && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>= {fC(parseFloat(form.amount) || 0)}</p>}
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Recognised Date</label>
              <input type="date" value={form.recognised_date} onChange={e => setForm(f => ({ ...f, recognised_date: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-recdate" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Invoice #</label>
              <input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="INV-2026-001" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-invoice" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>PO #</label>
              <input value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} placeholder="JLR-PO-Q1" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-po" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Q1 PO received, 430 RR + 520 RR Sport units delivered" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-form-notes" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={submit} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="b2b-form-submit">{editId ? 'Save' : 'Record'}</button>
            <button onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Realised Revenue ledger */}
      {entries.length > 0 && (
        <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }} data-testid="b2b-entries-list">
          <div className="px-4 py-2.5 border-b" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
              <Receipt size={12} /> Realised Revenue Ledger ({entries.length})
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead style={{ backgroundColor: '#F7F5F2' }}>
                <tr>
                  <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Month</th>
                  <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Contract</th>
                  <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Amount</th>
                  <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Invoice</th>
                  <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>PO</th>
                  <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {entries.map(e => {
                  const isProjected = e.entry_type === 'projected';
                  return (
                  <tr key={e.id} className="hover:bg-gray-50" data-testid={`b2b-entry-${e.id}`} style={isProjected ? { backgroundColor: '#FFFBEB' } : {}}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>
                      {e.month}
                      {isProjected && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#BA7517' }}>PROJECTED</span>}
                    </td>
                    <td className="px-3 py-2 truncate max-w-[160px]" style={{ color: '#6B7280' }}>{e.deal_name || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: isProjected ? '#BA7517' : '#1B2A4A', fontStyle: isProjected ? 'italic' : 'normal' }}>{fC(e.amount)}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.invoice_number || '—'}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.po_number || '—'}</td>
                    <td className="px-3 py-2 truncate max-w-[180px]" style={{ color: '#6B7280' }}>{e.notes || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => startEdit(e)} className="p-1 rounded hover:bg-gray-100" data-testid={`b2b-edit-${e.id}`}><Pencil size={11} style={{ color: '#6B7280' }} /></button>
                        <button onClick={() => remove(e)} className="p-1 rounded hover:bg-red-50" data-testid={`b2b-delete-${e.id}`}><Trash2 size={11} style={{ color: '#D4726A' }} /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
